import { Fragment, ReactNode, useState } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { cx } from '@libs/classnames';
import { useResizeObserver } from '@libs/hooks/useResizeObserver';
import { CapsuleButton } from '@ui/components/CapsuleButton';
import { CardProps } from '@ui/components/Card';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Loader } from '@ui/components/Loader';
import { PageComplexSubTitle } from '@ui/components/PageComplexSubTitle';
import { Placeholder } from '@ui/components/Placeholder/Placeholder';
import { SearchInput } from '@ui/components/SearchInput';
import { Tabs } from '@ui/components/Tabs';

import { EventListTab } from '@features/club/typings';

import { ClubEventCardProps } from '../ClubEventCard';
import { ClubEventCard } from '../ClubEventCard/ClubEventCard';
import { ITab, TABS } from './constants';

import styles from './ClubEventList.module.css';

export type Props = {
  id?: string;
  className?: string;
  title?: string;
  tabs?: ITab[];
  tab: string;
  subTab?: string;
  subtabs?: ITab[];
  loading?: boolean;
  events?: ClubEventCardProps['tournament'][];
  eventsTheme?: CardProps['theme'];
  customEventEntranceText?: string;
  onTab?: (t: string, s?: string) => void;
  getTournamentHref?: (tournamentId: string) => string;
  isManager?: boolean;
  query?: string;
  onQueryChange?: (query: string) => void;
  news?: (className?: string) => ReactNode;
  onLoadMore?: () => void;
};

export function ClubEventList({
  id,
  className,
  title = 'События',
  tabs = TABS,
  tab,
  subTab,
  subtabs,
  loading,
  events,
  eventsTheme,
  customEventEntranceText,
  onTab,
  getTournamentHref,
  onLoadMore,
  isManager,
  query,
  onQueryChange,
  news,
}: Props) {
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(true);
  const isMobile = useMediaQuery() === 'mobile';

  // Господи, переносилось и нормально было, на хрен это все понадобилось
  const [widthPreset, setWidthPreset] = useState<'s' | 'm' | 'l' | 'xl'>('m');
  const listRef = useResizeObserver<HTMLDivElement>(
    (entry) => {
      if (entry.contentRect.width < 819) {
        setWidthPreset('s');
      } else if (entry.contentRect.width < 888) {
        setWidthPreset('m');
      } else if (entry.contentRect.width < 1192) {
        setWidthPreset('l');
      } else {
        setWidthPreset('xl');
      }
    },
    [loading],
  );

  const newsAreVisible = news && tab === 'upcoming';
  const separateNews = newsAreVisible && (widthPreset === 's' || !events?.length);
  const newsInline = newsAreVisible && !separateNews;
  // m & xl = 1; s = hidden
  let newsInlinePosition = 1;
  if (widthPreset === 'l') newsInlinePosition = 2;
  newsInlinePosition = Math.min((events?.length || 0) - 1, newsInlinePosition);

  const singleColumn = isMobile && (tab === 'finished' || events?.length === 1);

  return (
    <div className={cx(className, styles.events)} id={id}>
      <div className={styles.header}>
        <PageComplexSubTitle
          title={title}
          filters={
            <Tabs
              className={cx(styles.tabs, {
                [styles.collapsed]: !isSearchCollapsed,
              })}
              tabClassName={cx({ [styles.managerTabItem]: isManager }, styles.tabItem)}
              noTabMinWidth
              flat
              tabs={tabs}
              value={tab}
              onChange={(newTab) => onTab?.(newTab as EventListTab)}
            />
          }
          subfilters={
            subtabs && (
              <HorizontalScroll enableForDesktop className={styles.schoolTabs}>
                <Tabs
                  className={styles.tabs}
                  flat
                  tabs={subtabs}
                  value={subTab}
                  onChange={(s) => onTab?.(tab, s)}
                  noTabMinWidth
                />
              </HorizontalScroll>
            )
          }
          actions={
            tab === 'finished' &&
            onQueryChange && (
              <SearchInput
                className={styles.searchInput}
                query={query}
                onQueryChange={onQueryChange}
                placeholder="Поиск по событиям"
                collapsible={isMobile}
                onCollapseChange={setIsSearchCollapsed}
              />
            )
          }
        />
      </div>

      {(loading && <Loader className={styles.loader} centered />) || (
        <HorizontalScroll
          ref={listRef}
          className={cx(styles.list, {
            [styles.singleColumn]: singleColumn && isMobile,
          })}
        >
          {events && events.length > 0 && (
            <>
              {events.map((evt, i) => (
                <Fragment key={evt.tournamentId}>
                  <ClubEventCard
                    tournament={evt}
                    getTournamentHref={getTournamentHref}
                    isManager={isManager}
                    wide={isMobile && singleColumn}
                    theme={eventsTheme}
                    customEntranceText={customEventEntranceText}
                    showSeal={
                      eventsTheme?.startsWith('grand-prix') || eventsTheme?.startsWith('premium')
                    }
                  />

                  {i === newsInlinePosition && newsInline && news(styles.news)}
                </Fragment>
              ))}
            </>
          )}
          {(!events || !events.length) && (
            <Placeholder>{query ? 'Турниры не найдены' : 'Пока нет турниров'}</Placeholder>
          )}
        </HorizontalScroll>
      )}

      {onLoadMore && (
        <div className={styles.loadMoreButtonContainer}>
          <CapsuleButton preset="gray" size="16m-40" onClick={onLoadMore}>
            Показать еще
          </CapsuleButton>
        </div>
      )}

      {separateNews && news(styles.news)}
    </div>
  );
}
